import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import PanelState, { extractApiError } from "@/components/PanelState";
import { RefreshCw, ChevronRight, AlertTriangle, Clock, LayoutList, LayoutGrid, Info, FlaskConical, Archive, Flag } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/lib/auth";
import { useHiringAccess } from "@/lib/hiringAccess";
import RowActionsMenu from "./hiring/RowActionsMenu";
import { PipelineBulkBar } from "./hiring/PipelineBulkBar";
import { RegionBadge } from "./hiring/RegionBadge";
import { StageTitle, stageLabel, useStageOwners } from "./hiring/stageTitles";

// ============================================================================
// Hiring Pipeline — Phase E UI (2026-08-24 pt.25).
// Primary at-a-glance surface. Nothing gets stalled.
// List + Board toggle; per-row SLA chip visible; breached rows visually loud.
// ============================================================================

const STAGE_LABELS = Object.fromEntries([1, 2, 3, 4, 5].map((n) => [n, stageLabel(n)]));

const SLA_META = {
  on_track:              { label: "On track",         chip: "text-[#166534] bg-[#dcfce7] border-[#86efac]",   loud: false },
  sla_breached:          { label: "Past SLA",         chip: "text-[#991b1b] bg-[#fee2e2] border-[#f87171]",   loud: true  },
  tier_1_breached:       { label: "Tier 1 escalated", chip: "text-[#92400e] bg-[#fef3c7] border-[#fbbf24]",   loud: true  },
  tier_2_reassigned:     { label: "Tier 2 reassigned",chip: "text-[#991b1b] bg-white border-2 border-[#dc2626]", loud: true },
  clock_paused_off_hours:{ label: "Clock paused",     chip: "text-[#4b5563] bg-[#f3f4f6] border-[#e5e7eb]",   loud: false },
  no_sla:                { label: "No SLA",           chip: "text-[#4b5563] bg-[#f3f4f6] border-[#e5e7eb]",   loud: false },
};

function fmtTimeInStage(secs) {
  if (secs == null) return "—";
  if (secs < 60) return "just now";
  if (secs < 3600) return `${Math.floor(secs / 60)}m`;
  if (secs < 86400) return `${Math.floor(secs / 3600)}h`;
  return `${Math.floor(secs / 86400)}d`;
}

function SlaChip({ state }) {
  const m = SLA_META[state] || SLA_META.on_track;
  return (
    <span
      className={`inline-block px-2 py-0.5 rounded-full text-xs border ${m.chip}`}
      data-testid={`hiring-pipeline-sla-chip-${state}`}
    >
      {m.label}
    </span>
  );
}

function BorderlineTag({ show }) {
  if (!show) return null;
  return (
    <span
      className="inline-flex items-center gap-1 text-[11px] text-[#b45309]"
      title="Borderline note — see detail"
      data-testid="hiring-pipeline-borderline-tag"
    >
      <Info className="w-3 h-3" /> borderline
    </span>
  );
}

function OwnerCell({ display, emails, compact }) {
  // CC-044 (2026-09-24) — prefer resolved names on the primary line
  // with the email as small grey subtext. Falls back cleanly if the
  // backend hasn't attached `current_owner_display` yet (older cached
  // response) — reads the raw emails list and echoes it as the name.
  const resolved = (display && display.length)
    ? display
    : (emails || []).map((e) => ({ email: e, name: e }));
  if (resolved.length === 0) {
    return <span className="text-[#9ca3af]">unassigned</span>;
  }
  const namesLine = resolved.map((o) => o.name).join(", ");
  const emailsLine = resolved.map((o) => o.email).join(", ");
  const hasResolvedName = resolved.some((o) => o.name && o.name !== o.email);
  return (
    <div>
      <div
        className={`text-[#0e1420] ${compact ? "text-xs" : "text-sm"} truncate`}
        data-testid="hiring-owner-name"
      >
        {namesLine}
      </div>
      {hasResolvedName && (
        <div
          className={`text-[#9ca3af] ${compact ? "text-[10px]" : "text-xs"} truncate`}
          data-testid="hiring-owner-email"
        >
          {emailsLine}
        </div>
      )}
    </div>
  );
}

function CandidateRow({ c, slug, isArchivedView, isPlatformAdmin, onChanged, selectable, selected, onToggle, owners }) {
  const loud = SLA_META[c.sla_state]?.loud;
  return (
    <tr
      className={`transition-colors ${loud ? "bg-[#fef2f2] border-l-4 border-[#dc2626]" : "hover:bg-[#f9fafb]"} ${isArchivedView ? "opacity-70" : ""}`}
      data-testid={`hiring-pipeline-row-${c.id}`}
    >
      {selectable && (
        <td className="pl-4 py-3 w-6">
          <input type="checkbox" checked={selected} onChange={() => onToggle(c.id)} aria-label={`Select ${c.contact?.name || c.id}`}
            data-testid={`hiring-pipeline-select-${c.id}`} />
        </td>
      )}
      <td className="px-4 py-3">
        <Link
          to={`/${slug}/app/hiring/candidates/${c.id}`}
          className="text-[#0e1420] font-medium hover:text-[#2563eb] flex items-center gap-2"
        >
          <RegionBadge region={c.region} testId={`hiring-pipeline-region-${c.id}`} />
          {c.contact?.name || "(no name)"}
          {c.flagged && (
            <span
              className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded font-semibold text-[#8a5a1a] bg-[#fef3d9] border border-[#e6c99a]"
              title={c.flagged_reason ? `Flagged: ${c.flagged_reason}` : "Flagged"}
              data-testid={`hiring-pipeline-flagged-badge-${c.id}`}
            >
              <Flag className="w-3 h-3" /> flagged
            </span>
          )}
          <BorderlineTag show={c.has_borderline_note} />
        </Link>
        <div className="text-xs text-[#4b5563]">{c.contact?.email || "—"}</div>
      </td>
      <td className="px-4 py-3 text-[#0e1420] text-sm">
        <StageTitle stage={c.stage} owners={owners} testId={`hiring-pipeline-stage-cell-${c.id}`} />
      </td>
      <td className="px-4 py-3 text-sm text-[#0e1420]">
        <OwnerCell display={c.current_owner_display} emails={c.current_owner_emails} />
      </td>
      <td className="px-4 py-3 text-sm text-[#4b5563]" title={c.stage_entered_at || ""}>
        <span className="inline-flex items-center gap-1"><Clock className="w-3.5 h-3.5" />{fmtTimeInStage(c.time_in_stage_seconds)}</span>
      </td>
      <td className="px-4 py-3">
        <SlaChip state={c.sla_state} />
      </td>
      <td className="px-4 py-3 text-right">
        <div className="inline-flex items-center gap-1">
          <RowActionsMenu
            candidate={c}
            isArchivedView={isArchivedView}
            isPlatformAdmin={isPlatformAdmin}
            onChanged={onChanged}
          />
          <Link to={`/${slug}/app/hiring/candidates/${c.id}`} className="text-[#4b5563] hover:text-[#2563eb]">
            <ChevronRight className="w-4 h-4" />
          </Link>
        </div>
      </td>
    </tr>
  );
}

function BoardCard({ c, slug, isArchivedView, isPlatformAdmin, onChanged }) {
  const loud = SLA_META[c.sla_state]?.loud;
  return (
    <div className={`relative ${isArchivedView ? "opacity-70" : ""}`}>
      <Link
        to={`/${slug}/app/hiring/candidates/${c.id}`}
        className={`block rounded-lg border p-3 mb-2 transition-shadow hover:shadow-sm ${loud ? "bg-[#fef2f2] border-[#dc2626]" : "bg-white border-[#e5e7eb]"}`}
        data-testid={`hiring-pipeline-card-${c.id}`}
      >
        <div className="flex items-start justify-between gap-2">
          <div className="text-sm font-medium text-[#0e1420]">{c.contact?.name || "(no name)"}</div>
          <SlaChip state={c.sla_state} />
        </div>
        <div className="text-xs mt-1">
          <OwnerCell display={c.current_owner_display} emails={c.current_owner_emails} compact />
        </div>
        <div className="text-xs text-[#4b5563] mt-1 inline-flex items-center gap-1"><Clock className="w-3 h-3" />{fmtTimeInStage(c.time_in_stage_seconds)}</div>
        <BorderlineTag show={c.has_borderline_note} />
      </Link>
      <div className="absolute top-2 right-2">
        <RowActionsMenu
          candidate={c}
          isArchivedView={isArchivedView}
          isPlatformAdmin={isPlatformAdmin}
          onChanged={onChanged}
        />
      </div>
    </div>
  );
}

export default function HiringPipeline() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isPlatformAdmin = user?.role === "platform_admin";
  const canWrite = useHiringAccess(user);  // CC-148
  const [state, setState] = useState({ loading: true, error: null });
  const [rows, setRows] = useState([]);
  const [filterStage, setFilterStage] = useState("all");
  const [viewMode, setViewMode] = useState("list");
  const [seeding, setSeeding] = useState(false);
  const [sortBy, setSortBy] = useState("sla");  // CC-266: "sla" (default) | "days"
  const [selected, setSelected] = useState(() => new Set());
  const owners = useStageOwners();

  async function load() {
    setState({ loading: true, error: null });
    try {
      // pt.46 — default: exclude archived. `Archived` tab requests `?status=archived`.
      const params = filterStage === "archived"
        ? { status: "archived" }
        : {};  // backend already excludes archived when no `status` param
      const { data } = await api.get("/hiring/candidates", { params });
      setRows(data?.candidates || []);
      setState({ loading: false, error: null });
    } catch (e) {
      setState({ loading: false, error: extractApiError(e) });
    }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [slug, filterStage]);
  useEffect(() => { setSelected(new Set()); }, [filterStage, rows]);

  async function seedTestCandidate() {
    if (seeding) return;
    setSeeding(true);
    try {
      const { data } = await api.post("/hiring/admin/seed-test-candidate", {});
      toast.success(`Seeded ${data.name} at Stage 4 — opening detail…`);
      navigate(`/${slug}/app/hiring/candidates/${data.candidate_id}`);
    } catch (e) {
      toast.error(extractApiError(e) || "Seed failed");
    } finally {
      setSeeding(false);
    }
  }

  const filtered = useMemo(() => {
    let r = rows;
    if (filterStage !== "all" && filterStage !== "archived") {
      r = r.filter((c) => String(c.stage) === filterStage);
    }
    // Sort: breached first (loud states), then oldest-in-stage first. CC-266: or longest in stage only.
    return [...r].sort((a, b) => {
      if (sortBy === "days") return (b.time_in_stage_seconds || 0) - (a.time_in_stage_seconds || 0);
      const aL = SLA_META[a.sla_state]?.loud ? 1 : 0;
      const bL = SLA_META[b.sla_state]?.loud ? 1 : 0;
      if (aL !== bL) return bL - aL;
      return (b.time_in_stage_seconds || 0) - (a.time_in_stage_seconds || 0);
    });
  }, [rows, filterStage, sortBy]);

  const isArchivedView = filterStage === "archived";
  const selectable = canWrite && !isArchivedView;
  const stageCounts = useMemo(() => rows.reduce((m, c) => ({ ...m, [String(c.stage)]: (m[String(c.stage)] || 0) + 1 }), {}), [rows]);
  const allSelected = filtered.length > 0 && filtered.every((c) => selected.has(c.id));
  const toggle = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(filtered.map((c) => c.id)));

  const breachCount = rows.filter((c) => SLA_META[c.sla_state]?.loud).length;
  const empty = !state.loading && !state.error && filtered.length === 0;

  return (
    <div className="space-y-6" data-testid="hiring-pipeline-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-[#0e1420]">Hiring — Pipeline</h1>
          <p className="text-sm text-[#4b5563] mt-1">Every in-progress candidate. Breach-first sort.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant={viewMode === "list" ? "default" : "outline"}
            size="sm"
            onClick={() => setViewMode("list")}
            data-testid="hiring-pipeline-view-list"
          >
            <LayoutList className="w-4 h-4 mr-1" /> List
          </Button>
          <Button
            variant={viewMode === "board" ? "default" : "outline"}
            size="sm"
            onClick={() => setViewMode("board")}
            data-testid="hiring-pipeline-view-board"
          >
            <LayoutGrid className="w-4 h-4 mr-1" /> Board
          </Button>
          <Button
            onClick={load}
            variant="outline"
            size="sm"
            data-testid="hiring-pipeline-refresh"
          >
            <RefreshCw className="w-4 h-4 mr-1" /> Refresh
          </Button>
          {canWrite && (
            <Button
              onClick={seedTestCandidate}
              variant="outline"
              size="sm"
              disabled={seeding}
              className="border-dashed"
              title="Create a throwaway Stage-4 candidate for post-deploy verification. Name matches the sweep pattern — auto-cleans on next restart."
              data-testid="hiring-pipeline-seed-test-candidate"
            >
              <FlaskConical className="w-4 h-4 mr-1" />
              {seeding ? "Seeding…" : "Seed test candidate"}
            </Button>
          )}
        </div>
      </div>

      {breachCount > 0 && (
        <div
          className="bg-[#fef2f2] border border-[#dc2626] rounded-lg p-3 flex items-center gap-2 text-sm text-[#991b1b]"
          data-testid="hiring-pipeline-breach-banner"
        >
          <AlertTriangle className="w-4 h-4" />
          <span><strong>{breachCount}</strong> candidate{breachCount === 1 ? "" : "s"} past SLA or escalated — sorted to top.</span>
        </div>
      )}

      <div className="flex gap-2 flex-wrap" data-testid="hiring-pipeline-stage-filter">
        {[
          { k: "all", label: "All" },
          { k: "1", label: "Stage 1" },
          { k: "2", label: "Stage 2" },
          { k: "3", label: "Stage 3" },
          { k: "4", label: "Stage 4" },
          { k: "5", label: "Stage 5" },
          { k: "archived", label: "Archived", icon: Archive },
        ].map((f) => (
          <button
            key={f.k}
            onClick={() => setFilterStage(f.k)}
            className={`px-3 py-1 rounded-full text-xs border transition-colors inline-flex items-center gap-1 ${filterStage === f.k ? "bg-[#0e1420] text-white border-[#0e1420]" : "bg-white text-[#4b5563] border-[#e5e7eb] hover:border-[#9ca3af]"}`}
            data-testid={`hiring-pipeline-filter-stage-${f.k}`}
          >
            {f.icon && <f.icon className="w-3 h-3" />}
            {/^\d$/.test(f.k)
              ? <StageTitle stage={Number(f.k)} owners={owners} testId={`hiring-pipeline-filter-title-${f.k}`} className="text-left" />
              : f.label}
            {!isArchivedView && f.k !== "archived" && (
              <span className="opacity-70" data-testid={`hiring-pipeline-filter-count-${f.k}`}>
                ({f.k === "all" ? rows.length : stageCounts[f.k] || 0})
              </span>
            )}
          </button>
        ))}
        <label className="ml-auto text-xs text-[#4b5563] inline-flex items-center gap-1">
          Sort
          <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} className="border border-[#e5e7eb] rounded px-1.5 py-1 text-xs"
            data-testid="hiring-pipeline-sort">
            <option value="sla">Past SLA first</option>
            <option value="days">Days in stage</option>
          </select>
        </label>
      </div>

      {selectable && (
        <PipelineBulkBar ids={[...selected]} onClear={() => setSelected(new Set())} onDone={load} />
      )}

      <PanelState
        status={state.loading ? "loading" : state.error ? "error" : "ok"}
        error={state.error}
        isEmpty={empty}
        onRetry={load}
        emptyTitle="No active candidates"
        emptyBody="New intakes will appear here once candidates submit their applications."
        testIdPrefix="hiring-pipeline"
      />

      {!state.loading && !state.error && filtered.length > 0 && viewMode === "list" && (
        <div className="bg-white rounded-lg border border-[#e5e7eb] overflow-hidden">
          <table className="min-w-full text-sm" data-testid="hiring-pipeline-list-table">
            <thead className="bg-[#f9fafb] text-left text-xs uppercase tracking-wide text-[#4b5563]">
              <tr>
                {selectable && (
                  <th className="pl-4 py-3 w-6">
                    <input type="checkbox" checked={allSelected} onChange={toggleAll} aria-label="Select all in this filter"
                      data-testid="hiring-pipeline-select-all" />
                  </th>
                )}
                <th className="px-4 py-3">Candidate</th>
                <th className="px-4 py-3">Stage</th>
                <th className="px-4 py-3">Current owner</th>
                <th className="px-4 py-3">In stage</th>
                <th className="px-4 py-3">SLA</th>
                <th className="px-4 py-3 w-6"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#e5e7eb]">
              {filtered.map((c) => <CandidateRow key={c.id} c={c} slug={slug} owners={owners} isArchivedView={isArchivedView} isPlatformAdmin={isPlatformAdmin}
                onChanged={load} selectable={selectable} selected={selected.has(c.id)} onToggle={toggle} />)}
            </tbody>
          </table>
        </div>
      )}

      {!state.loading && !state.error && filtered.length > 0 && viewMode === "board" && (
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4" data-testid="hiring-pipeline-board">
          {[1, 2, 3, 4, 5].map((stage) => {
            const stageCandidates = filtered.filter((c) => c.stage === stage);
            return (
              <div key={stage} className="bg-[#f9fafb] rounded-lg border border-[#e5e7eb] p-3" data-testid={`hiring-pipeline-board-column-${stage}`}>
                <div className="flex items-center justify-between mb-3">
                  <div className="text-xs uppercase tracking-wide text-[#4b5563]">
                    <StageTitle stage={stage} owners={owners} testId={`hiring-pipeline-board-title-${stage}`} />
                  </div>
                  <div className="text-xs text-[#9ca3af]">{stageCandidates.length}</div>
                </div>
                {stageCandidates.length === 0 ? (
                  <div className="text-xs text-[#9ca3af] italic">No candidates</div>
                ) : (
                  stageCandidates.map((c) => <BoardCard key={c.id} c={c} slug={slug} isArchivedView={isArchivedView} isPlatformAdmin={isPlatformAdmin} onChanged={load} />)
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
